import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../OnboardingContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Wallet, ChevronRight } from 'lucide-react';
import { useAutoScroll } from '@/hooks/useAutoScroll';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';

// Income range IDs (stored in English in backend)
const incomeRanges = [
  { id: 'under-25k', value: '<25000' },
  { id: '25k-100k', value: '25000-100000' },
  { id: 'over-100k', value: '>100000' },
];

// Expected monthly transaction volume tiers.
// `value` is a representative numeric string deliberately chosen so the
// calculate-individual-risk `normalizeMonthlyVolumeTier` parser
// (max-of-digits → bucket) maps each option back to the exact engine tier id.
const monthlyVolumeTiers = [
  { id: 'under_10k',   label: 'Under €10,000',          value: '5000' },
  { id: '10k_50k',     label: '€10,000 – €50,000',      value: '30000' },
  { id: '50k_100k',    label: '€50,000 – €100,000',     value: '75000' },
  { id: '100k_250k',   label: '€100,000 – €250,000',    value: '150000' },
  { id: '250k_500k',   label: '€250,000 – €500,000',    value: '350000' },
  { id: '500k_1m',     label: '€500,000 – €1,000,000',  value: '750000' },
  { id: 'over_1m',     label: 'Over €1,000,000',        value: '1500000' },
];

// Source of funds IDs (stored in English in backend)
const sourceOfFunds = [
  { id: 'salary' },
  { id: 'business' },
  { id: 'investments' },
  { id: 'inheritance' },
  { id: 'savings' },
  { id: 'pension' },
  { id: 'rental' },
  { id: 'other' },
];


export function IncomeStep() {
  const { t } = useTranslation();
  const { goToNextStep, updateData, data } = useOnboarding();
  const { scrollToRef } = useAutoScroll();
  const { canActivate, recheckScroll } = useScrollToActivate();
  
  // Initialize from context to preserve data on back navigation
  const [selectedIncome, setSelectedIncome] = useState(() => data.annualIncome || '');
  const [selectedSources, setSelectedSources] = useState<string[]>(() => data.sourceOfFunds || []);
  const [otherSourceDetails, setOtherSourceDetails] = useState(() => data.otherSourceOfFunds || '');
  const [selectedVolume, setSelectedVolume] = useState<string>(() => data.expectedMonthlyVolume || '');

  // Refs for auto-scrolling
  const otherDetailsRef = useRef<HTMLDivElement>(null);

  // Get translated label for income range
  const getIncomeLabel = (rangeId: string) => {
    const keyMap: Record<string, string> = {
      'under-25k': 'under25k',
      '25k-100k': '25k100k',
      'over-100k': 'over100k',
    };
    return t(`income.incomeRanges.${keyMap[rangeId] || rangeId}`);
  };


  // Get translated label for source of funds
  const getSourceLabel = (sourceId: string) => {
    return t(`income.sources.${sourceId}`);
  };

  const toggleSource = (sourceId: string) => {
    const newSources = selectedSources.includes(sourceId)
      ? selectedSources.filter(s => s !== sourceId)
      : [...selectedSources, sourceId];
    setSelectedSources(newSources);
    
    // Auto-scroll when "Other" is selected
    if (sourceId === 'other' && !selectedSources.includes('other')) {
      setTimeout(() => {
        scrollToRef(otherDetailsRef);
        recheckScroll();
      }, 150);
    }
  };

  const isOtherSelected = selectedSources.includes('other');
  const isFormValid =
    selectedIncome &&
    selectedSources.length > 0 &&
    selectedVolume &&
    (!isOtherSelected || otherSourceDetails.trim());

  const handleContinue = () => {
    updateData({
      annualIncome: selectedIncome, // Stored in English
      sourceOfFunds: selectedSources, // Stored as English IDs
      otherSourceOfFunds: isOtherSelected ? otherSourceDetails.trim() : undefined,
      expectedMonthlyVolume: selectedVolume, // Numeric string consumed by VOLUME_TIER normaliser
    });
    goToNextStep();
  };


  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
          <Wallet className="w-8 h-8 text-accent" />
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-3">
          {t('income.title')}
        </h1>
        
        <p className="text-muted-foreground mb-6">
          {t('income.subtitle')}
        </p>

        <div className="space-y-3 mb-8">
          {incomeRanges.map((range) => (
            <button
              key={range.id}
              onClick={() => setSelectedIncome(range.value)}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                selectedIncome === range.value
                  ? 'border-accent bg-accent/10'
                  : 'border-border bg-muted/20 hover:border-muted-foreground/30'
              }`}
            >
              <span className="font-medium text-foreground">{getIncomeLabel(range.id)}</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          ))}
        </div>

        <h2 className="text-lg font-semibold text-foreground mb-3">
          {t('income.sourceTitle')}
        </h2>
        
        <p className="text-muted-foreground text-sm mb-4">
          {t('income.sourceSubtitle')}
        </p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          {sourceOfFunds.map((source) => (
            <button
              key={source.id}
              onClick={() => toggleSource(source.id)}
              className={`p-3 rounded-xl border-2 transition-all text-sm ${
                selectedSources.includes(source.id)
                  ? 'border-accent bg-accent/10'
                  : 'border-border bg-muted/20 hover:border-muted-foreground/30'
              }`}
            >
              <span className="font-medium text-foreground">{getSourceLabel(source.id)}</span>
            </button>
          ))}
        </div>

        {isOtherSelected && (
          <div ref={otherDetailsRef} className="mb-6 animate-in fade-in slide-in-from-top-2 duration-200">
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('income.otherSourceLabel')}
            </label>
            <Textarea
              value={otherSourceDetails}
              onChange={(e) => setOtherSourceDetails(e.target.value)}
              placeholder={t('income.otherSourcePlaceholder')}
              className="min-h-[100px] rounded-xl border-2 border-border bg-muted/20 focus:border-accent"
              autoFocus
            />
          </div>
        )}

        {/* Expected monthly transaction volume — feeds VOLUME_TIER */}
        <h2 className="text-lg font-semibold text-foreground mb-3 mt-2">
          Expected monthly transaction volume
        </h2>
        <p className="text-muted-foreground text-sm mb-4">
          Your best estimate of total funds flowing through the account each month.
        </p>
        <div className="space-y-3 mb-8">
          {monthlyVolumeTiers.map((tier) => (
            <button
              key={tier.id}
              onClick={() => setSelectedVolume(tier.value)}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                selectedVolume === tier.value
                  ? 'border-accent bg-accent/10'
                  : 'border-border bg-muted/20 hover:border-muted-foreground/30'
              }`}
            >
              <span className="font-medium text-foreground">{tier.label}</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          ))}
        </div>

      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleContinue}
            disabled={!isFormValid || !canActivate}
            className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
}
